import { API_URL } from "../../constants/constants";
import { ResponseDataType, listDataType } from "../../types";
import { apiSlice } from "../api/apiSlice";

const apiWithTags = apiSlice.enhanceEndpoints({
  addTagTypes: [
    "saasToolWiseCostList",
    "costHistory",
    "costReport",
    "cloudBudgetList",
    "cloudBudgetReport",
    "singleCloudBudget",
    "cloudBudgetYearlyReport",
  ],
});

export const costSlice = apiWithTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Fetches the list of costs from the server.
     * @param search - The search query to filter the costs.
     * @param limit - The maximum number of costs to fetch.
     * @param current_page - The current page of the cost list.
     * @param sort_by - The field to sort the costs by.
     * @param sort_direction - The direction to sort the costs in (ascending or descending).
     */
    saasToolWiseCostList: builder.query<ResponseDataType, listDataType>({
      query: ({
        search,
        limit,
        current_page,
        sort_by,
        sort_direction
      }) => ({
        url:
          API_URL.COST +
          "/current" +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}`,
        method: "GET",
      }),
      providesTags: ["saasToolWiseCostList"],
    }),

    /**
     * Fetches the cost history from the server.
     * @param search - The search query to filter the costs.
     * @param limit - The maximum number of costs to fetch.
     * @param current_page - The current page of the cost list.
     * @param sort_by - The field to sort the costs by.
     * @param sort_direction - The direction to sort the costs in (ascending or descending).
     * @param start_date - The start date for the cost history.
     * @param end_date - The end date for the cost history.
     * @param tool_names - The names of the tools to filter the costs.
     */

    costHistory: builder.query<ResponseDataType, listDataType>({
      query: ({
        search,
        limit,
        current_page,
        sort_by,
        sort_direction,
        start_date,
        end_date,
        tool_names,
      }) => ({
        url:
          API_URL.COST +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}${start_date ? `&start_date=${start_date}` : ""
          }${end_date ? `&end_date=${end_date}` : ""}${tool_names ? `&tool_names=${tool_names}` : ""
          }`,
        method: "GET",
      }),
      providesTags: ["costHistory"],
    }),

    /**
     * Fetches the cost report from the server.
     * @param tool_name - The name of the tool to filter the costs.
     * @param number_of_months - The number of months to include in the report.
     */

    costReport: builder.query<ResponseDataType, listDataType>({
      query: ({ tool_name, number_of_months }) => ({
        url:
          API_URL.COST +
          `/report` +
          `?${tool_name ? `tool_name=${tool_name}` : ""}${number_of_months ? `&number_of_months=${number_of_months}` : ""
          }`,
        method: "GET",
      }),

      providesTags: ["costReport"],
    }),

    /**
     * Fetches the list of cloud budgets from the server.
     * @param search - The search query to filter the cloud budgets.
     * @param limit - The maximum number of cloud budgets to fetch.
     * @param current_page - The current page of the cloud budget list.
     * @param sort_by - The field to sort the cloud budgets by.
     * @param sort_direction - The direction to sort the cloud budgets in (ascending or descending).
     * @param start_date - The start date for the cloud budget list.
     * @param end_date - The end date for the cloud budget list.
     */

    cloudBudgetList: builder.query<ResponseDataType, listDataType>({
      query: ({
        search,
        limit,
        current_page,
        sort_by,
        sort_direction,
        start_date,
        end_date,
      }) => ({
        url:
          API_URL.CLOUD_BUDGET +
          "/" +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}${start_date ? `&start_date=${start_date}` : ""
          }${end_date ? `&end_date=${end_date}` : ""}`,
        method: "GET",
      }),
      providesTags: ["cloudBudgetList"],
    }),

    /**
     * Adds a new cloud budget to the server.
     * @param formData - The form data containing the cloud budget details.
     */
    addCloudBudget: builder.mutation<ResponseDataType, FormData>({
      query: (formData) => ({
        url: API_URL.CLOUD_BUDGET,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [
        "cloudBudgetList",
        "cloudBudgetReport",
        "singleCloudBudget",
      ],
    }),

    /**
     * Fetches a single cloud budget by its ID.
     * @param id - The ID of the cloud budget to fetch.
     */
    getCloudBudget: builder.query<ResponseDataType, string>({
      query: (id) => ({
        url: API_URL.CLOUD_BUDGET + `/${id}`,
        method: "GET",
      }),
      providesTags: ["singleCloudBudget"],
    }),

    /**
     * Updates a cloud budget.
     * @param id - The ID of the cloud budget to update.
     * @param formData - The form data containing the updated cloud budget details.
     */

    updateCloudBudget: builder.mutation<
      ResponseDataType,
      { id: string; formData: FormData }
    >({
      query: ({ id, formData }) => ({
        url: API_URL.CLOUD_BUDGET + `/${id}`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [
        "cloudBudgetList",
        "cloudBudgetReport",
        "singleCloudBudget",
      ],
    }),

    /**
     * Deletes a cloud budget from the server.
     * @param id - The ID of the cloud budget to delete.
     */

    deleteCloudBudget: builder.mutation<ResponseDataType, string>({
      query: (id) => ({
        url: API_URL.CLOUD_BUDGET + `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "cloudBudgetList",
        "cloudBudgetReport",
        "singleCloudBudget",
      ],
    }),

    /**
     * Fetches the cloud budget report from the server.
     * @param tool_name - The name of the tool to filter the cloud budgets.
     * @param number_of_months - The number of months to include in the report.
     * @param type - The type of the report to fetch.
     */

    cloudBudgetReport: builder.query<ResponseDataType, listDataType>({
      query: ({ tool_name, number_of_months, type }) => ({
        url:
          API_URL.CLOUD_BUDGET +
          `/report` +
          `?${tool_name ? `tool_name=${tool_name}` : ""}${number_of_months ? `&number_of_months=${number_of_months}` : ""
          }${type ? `&type=${type}` : ""}`,
        method: "GET",
      }),

      providesTags: ["cloudBudgetReport"],
    }),

    /**
     * Fetches the cloud budget yearly report from the server.
     * @param year - The year to fetch the report for.
     */

    cloudBudgetYearlyReport: builder.query<ResponseDataType, listDataType>({
      query: ({ year }) => ({
        url:
          API_URL.CLOUD_BUDGET +
          `/yearly-report` +
          `?${year ? `year=${year}` : ""}`,
        method: "GET",
      }),

      providesTags: ["cloudBudgetYearlyReport"],
    }),
  }),
});

export const {
  useSaasToolWiseCostListQuery,
  useCostHistoryQuery,
  useCostReportQuery,
  useCloudBudgetListQuery,
  useAddCloudBudgetMutation,
  useGetCloudBudgetQuery,
  useUpdateCloudBudgetMutation,
  useDeleteCloudBudgetMutation,
  useCloudBudgetReportQuery,
  useCloudBudgetYearlyReportQuery,
} = costSlice;
